// 卡住处理（原设计 8.1）：四个大按钮 + 放弃 / 重新执行；「我来处理」先接管再打开可操作实时画面
import { useNavigate } from "react-router-dom";
import type { TaskDTO } from "@csp/shared";
import { api, type StuckAction } from "../lib/api";
import { useMediaToken } from "../lib/media";
import { useAction } from "../lib/hooks";
import { mediaUrl } from "../lib/net";
import { useDialog } from "./Dialogs";
import { useLightbox } from "./Lightbox";
import { VncViewer } from "./VncViewer";

import { isTechnicalPause } from "../lib/pauseReason";
import { RESUME_AFTER_HANDLING } from "./ExplorationQuestion";

const PROCESS_PREFIX = "正确做法是：";

export function StuckPanel({
  task,
  takeover,
  lastShot,
  canAct,
  onChanged,
  externalScreen = false,
}: {
  task: TaskDTO;
  takeover: boolean;
  lastShot: string | null;
  canAct: boolean;
  onChanged: () => void;
  externalScreen?: boolean;
}) {
  const dialog = useDialog();
  const navigate = useNavigate();
  const lb = useLightbox();
  const act = useAction();
  const media = useMediaToken();
  const shot = media ? mediaUrl(lastShot, media) : "";
  const technical = isTechnicalPause(task.blockReason || "");
  const escalated = task.escalated;

  const send = async (action: StuckAction, note?: string) => {
    const r = await act.run(action, () => api.stuck(task.id, action, note));
    if (r) onChanged();
    return !!r;
  };

  const answer = async () => {
    const note = await dialog.prompt({ title: "补充资料并继续探索", body: <p translate="no">{task.blockReason}</p>, label: "你的回答", required: true, okText: "继续探索" });
    if (!note) return;
    const r = await act.run("answer", () => api.learningAnswer(task.id, note));
    if (r) onChanged();
  };
  const resume = async () => {
    const r = await act.run("resume", () => api.learningAnswer(task.id, RESUME_AFTER_HANDLING));
    if (r) onChanged();
  };
  const instruct = async () => {
    const note = await dialog.prompt({ title: "指示它怎么做", body: <p>输入接下来要做什么，确认后从当前暂停处继续；指示会保存到事件记录。</p>, label: "接下来要做什么", required: true, okText: "按指示继续" });
    if (!note) return;
    if (task.learningRun) {
      const r = await act.run("instruct", () => api.learningAnswer(task.id, note));
      if (r) onChanged();
    } else await send("process", note);
  };
  const handleSelf = async () => {
    await send("takeover");
  };
  const handled = async () => {
    const note = await dialog.prompt({ title: "处理完了，继续", label: "你做了什么（一句话）", placeholder: "例如：手动点了 Next", okText: "让 AI 继续" });
    if (note !== null) await send("handled", note || undefined);
  };
  const dataProblem = async () => {
    const note = await dialog.prompt({ title: "材料有问题", label: "哪里有问题", placeholder: "例如：邮编填错了", required: true, okText: "去改资料" });
    if (note && (await send("data", note))) navigate(`/tasks/${task.id}/edit`);
  };
  const pageChanged = async () => {
    const note = await dialog.prompt({
      title: "页面和以前不一样",
      body: <p>告诉 AI 这一步现在该怎么做，它会照做，并提出 SOP 修改建议。</p>,
      label: "正确做法是…",
      initial: PROCESS_PREFIX,
      required: true,
      okText: "告诉 AI",
    });
    if (!note) return;
    const text = note.replace(/^正确做法是[:：]?\s*/, "").trim();
    if (!text) {
      act.setError("请写清楚正确做法");
      return;
    }
    await send("process", text);
  };
  const unknown = async () => {
    const note = await dialog.prompt({ title: "不知道，找管理员", label: "补充说明（可不填）", okText: "交给管理员" });
    if (note !== null) await send("unknown", note || undefined);
  };
  const giveUp = async () => {
    const note = await dialog.prompt({ title: "放弃任务？", label: "原因（可不填）", okText: "放弃", danger: true, cancelText: "不放弃" });
    if (note !== null) await send("give_up", note || undefined);
  };
  const retry = async () => {
    const ok = await dialog.confirm({ title: "重新执行？", body: <p>从头再跑一次（新开一次运行）。</p>, okText: "重新执行" });
    if (ok) await send("retry");
  };

  return (
    <div data-testid="stuck-panel">
      <h2 className={task.learningRun ? "" : "err"}>{technical ? "运行环境 / 工具异常" : task.learningRun ? "等待你指引下一步" : "⛔ 卡住了"}</h2>
      <div className={`notice ${task.learningRun ? "warn" : "err"}`} data-testid="block-reason">
        原因：{task.blockReason ?? "（未说明）"}
      </div>
      {escalated && <div className="notice warn">已交给管理员处理。</div>}
      {takeover ? (
        <>
          {!externalScreen && <VncViewer taskId={task.id} mode="control" />}
          <div className="row" style={{ marginTop: 12 }}>
            <button className="btn btn-ok btn-big" disabled={!canAct || !!act.busy} onClick={() => void handled()}>
              ✅ 处理完了，继续
            </button>
          </div>
        </>
      ) : (
        shot && (
          <div style={{ marginBottom: 14 }}>
            <div className="small muted">最后截图（点图放大）</div>
            <img className="shot" style={{ maxHeight: 360, objectFit: "contain", objectPosition: "top left" }} src={shot} alt="卡住时截图" onClick={() => lb.open(shot)} />
          </div>
        )
      )}
      {task.learningRun && canAct && !takeover && <button className="btn btn-ok" disabled={!!act.busy} onClick={() => void resume()}>问题已处理，继续</button>}
      {task.learningRun && canAct && !technical && <button className="btn btn-primary" disabled={!!act.busy} onClick={() => void answer()}>补充资料并继续探索</button>}
      {act.error && <div className="notice err" style={{ marginTop: 12 }}>{act.error}</div>}
      {!canAct && <div className="notice warn">卡住的任务由文员处理。</div>}
      {!takeover && canAct && (
        <div className="btn-grid" style={{ marginTop: 12 }}>
          {!technical && <button className="btn btn-primary btn-big" disabled={!!act.busy} onClick={() => void instruct()}>
            指示它怎么做
            <small>输入接下来要做什么，从当前暂停处继续</small>
          </button>}
          <button className="btn btn-primary btn-big" disabled={!!act.busy} onClick={() => void handleSelf()}>
            🖐 我来处理
            <small>打开实时画面，自己操作</small>
          </button>
          <button className="btn btn-big" disabled={!!act.busy} onClick={() => void dataProblem()}>
            📄 材料有问题
            <small>改资料后重跑</small>
          </button>
          <button className="btn btn-big" disabled={!!act.busy} onClick={() => void pageChanged()}>
            🔀 页面和以前不一样
            <small>告诉 AI 正确做法</small>
          </button>
          <button className="btn btn-big" disabled={!!act.busy} onClick={() => void unknown()}>
            🙋 不知道，找管理员
          </button>
        </div>
      )}
      {canAct && (
        <div className="row" style={{ marginTop: 14 }}>
          <button className="btn" disabled={!!act.busy} onClick={() => void retry()}>
            ↻ 重新执行
          </button>
          <button className="btn btn-danger" disabled={!!act.busy} onClick={() => void giveUp()}>
            放弃任务
          </button>
        </div>
      )}
      {lb.node}
    </div>
  );
}
